import type { AuthResponse, Usuario, Cartorio } from '~/types/auth'

export function useAuthStorage() {
  const AUTH_STORAGE_KEY = 'auth_data'
  const TOKEN_STORAGE_KEY = 'auth_token'

  const saveAuthData = (authData: AuthResponse) => {
    if (process.client) {
      try {
        if (!authData || typeof authData !== 'object') {
          throw new Error('Invalid auth data provided')
        }
        
        const jsonString = JSON.stringify(authData)
        localStorage.setItem(AUTH_STORAGE_KEY, jsonString)
        localStorage.setItem(TOKEN_STORAGE_KEY, authData.token)
      } catch (error) {
        console.error('Error saving auth data:', error)

        localStorage.removeItem(AUTH_STORAGE_KEY)
        localStorage.removeItem(TOKEN_STORAGE_KEY)
        throw error
      }
    }
  }

  const getAuthData = (): AuthResponse | null => {
    if (process.client) {
      try {
        const data = localStorage.getItem(AUTH_STORAGE_KEY)
        if (!data) return null
        
        return JSON.parse(data)
      } catch (error) {
        console.error('Error parsing auth data from localStorage:', error)

        localStorage.removeItem(AUTH_STORAGE_KEY)
        localStorage.removeItem(TOKEN_STORAGE_KEY)
        return null
      }
    }
    return null
  }

  const getToken = (): string | null => {
    if (process.client) {
      return localStorage.getItem(TOKEN_STORAGE_KEY)
    }
    return null
  }

  const getCurrentUser = (): Usuario | null => {
    const authData = getAuthData()
    return authData?.usuario || null
  }

  const getCurrentCartorio = (): Cartorio | null => {
    const authData = getAuthData()
    return authData?.cartorio || null
  }

  const getCartorios = (): Cartorio[] => {
    const authData = getAuthData()
    return authData?.cartorios || []
  }

  const isAuthenticated = (): boolean => {
    try {
      const token = getToken()
      const authData = getAuthData()
      
      return !!(token && authData)
    } catch (error) {
      console.error('Error checking authentication status:', error)
      return false
    }
  }

  const clearAuthData = () => {
    if (process.client) {
      localStorage.removeItem(AUTH_STORAGE_KEY)
      localStorage.removeItem(TOKEN_STORAGE_KEY)
    }
  }

  const debugAuthData = () => {
    if (process.client) {
      const authData = localStorage.getItem(AUTH_STORAGE_KEY)
      const token = localStorage.getItem(TOKEN_STORAGE_KEY)
      
      console.log('Auth Data Length:', authData?.length || 0)
      console.log('Token exists:', !!token)
      
      if (authData) {
        try {
          const parsed = JSON.parse(authData)
          console.log('Auth Data is valid JSON')
        } catch (error) {
          console.error('Auth Data is corrupted:', error)
          console.log('Raw auth data (first 100 chars):', authData.substring(0, 100))
        }
      }
    }
  }

  const hasPermission = (especialidade: string, action: 'visualizar' | 'inserir' | 'editar' | 'excluir' = 'visualizar'): boolean => {
    const cartorio = getCurrentCartorio()
    if (!cartorio) return false

    const permissao = cartorio.permissoes.find(p => p.especialidade_nome === especialidade)
    return permissao ? permissao[action] === 1 : false
  }

  const isAdmin = (): boolean => {
    const user = getCurrentUser()
    return user?.admin === 'S'
  }

  const setCurrentCartorio = (cartorioId: string): boolean => {
    try {
      const authData = getAuthData()
      if (!authData) return false

      const cartorio = authData.cartorios.find(c => c.id === cartorioId)
      if (!cartorio) return false

      authData.cartorio = cartorio

      saveAuthData(authData)
      
      return true
    } catch (error) {
      console.error('Error setting current cartorio:', error)
      return false
    }
  }

  return {
    saveAuthData,
    getAuthData,
    getToken,
    getCurrentUser,
    getCurrentCartorio,
    getCartorios,
    isAuthenticated,
    clearAuthData,
    debugAuthData,
    hasPermission,
    isAdmin,
    setCurrentCartorio
  }
} 